import AppButton from '../../../components/AppButton'
import AppTabs from '../../../components/AppTabs';
import './HomeClient.scss'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AppointmentData, ClientRequestType, CompanyPublicData } from '../../../models'
import { useDispatch, useSelector } from 'react-redux'
import { getDateString, getNowDateString } from '../../../shared/utils'
import { loadEntities, updateEntity } from '../../../store/entities/actions'
import { deleteAppointmentAsync, getCompanyPublicDataAsync, getMyAppointmentsAsync, getProfessionalsByIdAsync } from '../../../ajax/requests'
import { getEntitiesDataItems, isLoadingEntities } from '../../../store/entities/selectors'
import { AppointmentEntity, CompanyEntity, ProfessionalEntity } from '../../../store/entities/state'
import TitledContainer from '../../../components/TitledContainer'
import Typography from '../../../components/Typography'
import { useTranslations } from '../../../store/translations/hooks'
import { TK } from '../../../store/translations/translationKeys'
import { Spinner } from 'reactstrap'
import { Tabs, Tab } from '@material-ui/core'
import { AppointmentsList } from './AppointmentsList'
import { SelfScheduleModal } from './SelfScheduleModal'
import { getLoggedUserCompanies } from '../../../store/session/selectors'

const todayDate = getNowDateString()

const ClientHome: React.FC = () => {
    const dispatch = useDispatch()
    const t = useTranslations()

    const [companyId, setCompanyId] = useState<string|undefined>(undefined)
    const [scheduleForCompanyId, setScheduleForCompanyId] = useState<string|undefined>(undefined)

    const companiesIds = useSelector(getLoggedUserCompanies)
    const allAppointments = useSelector(getEntitiesDataItems<AppointmentEntity>('appointments'))
    const companies = useSelector(getEntitiesDataItems<CompanyEntity>('companies'))
    const professionals = useSelector(getEntitiesDataItems<ProfessionalEntity>('professionals'))
    const loadingAppointments = useSelector(isLoadingEntities('appointments'))

    const appointmentsByCompany = useMemo(() => allAppointments.reduce<Record<string, {next: AppointmentEntity[], previous: AppointmentData[]}>>((ret, a) => !a.companyId ? ret : {
        ...ret,
        [a.companyId]: {
            next: a.date >= todayDate ? [...(ret[a.companyId]?.next ?? []), a] : ret[a.companyId]?.next ?? [],
            previous: a.date < todayDate ? [...(ret[a.companyId]?.previous ?? []), a] : ret[a.companyId]?.previous ?? [],
        },
    }, {}), [allAppointments])

    const companiesById = useMemo(() => companies.reduce<Record<string, CompanyEntity>>(
        (ret, c) => ({...ret, [c.id]: c}), {}), 
        [companies],
    )

    const professionalsById = useMemo(() => professionals.reduce<Record<string, ProfessionalEntity>>(
        (ret, c) => ({...ret, [c.id]: c}), {}), 
        [professionals],
    )

    useEffect(() => {
        const from = new Date(new Date().getFullYear()-1, 0, 1)
        const to = new Date(new Date().getFullYear()+1, 11, 31)
        const fromS = getDateString(from)
        const toS = getDateString(to)

        for (const companyId of companiesIds.filter(a => !companies.map(c => c.id).includes(a))) {
            dispatch(loadEntities('companies', () => getCompanyPublicDataAsync(companyId), 'AppendResult'))
        }
        companiesIds.length && setCompanyId(companiesIds[0])

        dispatch(loadEntities('appointments', () => getMyAppointmentsAsync(fromS, toS)
            .then(ret => {
                const allProfessionals = Array.from(new Set(ret.map(a => a.professionalId)))
                dispatch(loadEntities('professionals', () => getProfessionalsByIdAsync(allProfessionals), 'AppendResult')) 
                return ret
            }), 'NewResult'))
    }, [])

    const scheduleClick = useCallback((companyId: string) => () => setScheduleForCompanyId(companyId), [setScheduleForCompanyId])

    const handleAppointmentCancel = useCallback((appointment: AppointmentEntity) => {
        dispatch(updateEntity('appointments', {...appointment, clientRequest: ClientRequestType.Cancel}, () => deleteAppointmentAsync(appointment.id)))
    }, [])

    const companySettings = scheduleForCompanyId && companiesById[scheduleForCompanyId] || undefined

    return (<div className='clientHome'>
        <AppTabs value={companyId ?? false} onChange={(_, v) => setCompanyId(v)} indicatorColor="primary" textColor="primary" style={{ borderBottom: 'var(--border-thin) solid var(--appLigthGrey)' }}>
            {companiesIds.map(id => (
                <Tab key={id} value={id} label={
                    !companiesById[id] && false ? t(TK.loading)
                        : companiesById[id]?.logo ? <img src={companiesById[id].logo}/>
                        : <Typography size='xx-large'>{companiesById[id]?.name ?? <Spinner size='md'/>}</Typography>
                } />
            ))}
        </AppTabs>
        <div className='adminTabsContent'>
            {companiesIds.map(id => companyId === id && (
                <div key={id}>
                    {companiesById[id]?.clientsLogin?.selfScheduling?.enabled && <div className='clientHomeButtonsBar'>
                        <AppButton size='lg' color='success' onClick={scheduleClick(id)}>{t(TK.schedule)}</AppButton>
                    </div>}
                    {!!appointmentsByCompany[id]?.next.length && <TitledContainer title={t(TK.yourNextAppointments)}>
                        <AppointmentsList professionals={professionalsById} appointments={appointmentsByCompany[id]?.next} onCancel={handleAppointmentCancel}/>
                    </TitledContainer> }
                    <hr/>
                    {!!appointmentsByCompany[id]?.previous.length && <TitledContainer title={t(TK.previousAppointments)}>
                        <AppointmentsList professionals={professionalsById} appointments={appointmentsByCompany[id]?.previous}/>
                    </TitledContainer> }
                    {!appointmentsByCompany[id]?.next.length && !appointmentsByCompany[id]?.previous.length && !loadingAppointments && <Typography>{t(TK.noClientAppointmentsNote)}</Typography>}
                    <hr/>
                    {!!companiesById[id]?.contacts && !!Object.keys(companiesById[id].contacts).length && <div className='contacts'>
                        {Object.entries(companiesById[id].contacts).map(([name, contact]) => (
                            <span key={name}><b>{name}:</b> {contact}</span>
                        ))}
                    </div>}
                </div>
            ))}
        </div>
        
        {loadingAppointments && <Typography style={{position: 'fixed', bottom: 0, left: 0, backgroundColor: 'white'}}>{t(TK.loading)}...</Typography>}

        {companySettings && <SelfScheduleModal 
            isOpen={!!scheduleForCompanyId} 
            toggle={() => setScheduleForCompanyId(undefined)} 
            company={companySettings}
        />}
    </div>)
}


export default ClientHome